import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Star, Trash2, ArrowLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminReviewsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/reviews`);
      setReviews(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchReviews();
  }, [user, authLoading, navigate, fetchReviews]);

  const handleDelete = async (review) => {
    if (!window.confirm(`Delete review by ${review.user_name || "Anonymous"}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/reviews/${review.id}`);
      await fetchReviews();
    } catch (e) {
      console.error(e);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}><div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" /></div>;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <button onClick={() => navigate("/admin")} className="flex items-center text-[#A1A1A1] hover:text-[#39FF14] font-mono text-xs uppercase tracking-wider mb-3" data-testid="back-to-admin">
          <ArrowLeft className="h-3 w-3 mr-2" /> Back to Admin
        </button>
        <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Admin</p>
        <h1 data-testid="reviews-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2] mb-8" style={{ fontFamily: "Chivo, sans-serif" }}>
          Product Reviews
        </h1>

        {reviews.length === 0 ? (
          <p className="bg-[#0D0D0D] border border-[#1A1A1A] p-6 text-[#A1A1A1] text-sm font-mono text-center" data-testid="reviews-empty">
            No reviews submitted yet.
          </p>
        ) : (
          <div className="bg-[#0D0D0D] border border-[#1A1A1A]" data-testid="admin-reviews-list">
            {reviews.map(r => (
              <div key={r.id} data-testid={`admin-review-${r.id}`} className="px-5 py-4 border-b border-[#1A1A1A] last:border-b-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                      <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map(n => (
                          <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? "fill-[#39FF14] text-[#39FF14]" : "text-[#1A1A1A]"}`} strokeWidth={1.5} />
                        ))}
                      </div>
                      {r.title && <span className="font-bold text-[#F2F2F2] text-sm">{r.title}</span>}
                      <span className="font-mono text-[10px] text-[#39FF14] uppercase tracking-wider">{r.product_name}</span>
                    </div>
                    {r.body && <p className="text-[#F2F2F2] text-sm whitespace-pre-wrap mb-2">{r.body}</p>}
                    <p className="font-mono text-[10px] text-[#A1A1A1] uppercase tracking-wider">
                      {r.user_name || "Anonymous"} · {new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDelete(r)}
                    title="Delete review"
                    data-testid={`delete-review-${r.id}`}
                    className="p-2 text-[#A1A1A1] hover:text-red-400 transition-colors shrink-0"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
